import Tip from '@components/tip/tip.component'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './payment-status.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  status: string
  paymentType: string
  hasNf?: boolean
  disableTip?: boolean
}

export default ({ status, paymentType, hasNf, disableTip = false }: Props) => {
  const [t] = useTranslation('paymentStatus')

  const [state, setState] = useState('')

  useEffect(() => {
    // Esse primeiro if com o status documentSent é um status que só existe no front
    if (status === 'pending' && paymentType === 'nf' && hasNf) {
      setState('documentSent')
    } else if (status !== state) {
      setState(status)
    }
  }, [status, paymentType, hasNf, state])

  const paymentStatus: any = useMemo(
    () => ({
      new: {
        text: t(`new.text.${paymentType}`),
        tip: t(`new.tip.${paymentType}`),
        icon: 'fa-solid fa-clock',
        color: 'var(--gray)',
      },
      pending: {
        text: t(`pending.text.${paymentType}`),
        tip: t(`pending.tip.${paymentType}`),
        icon: 'fa-solid fa-triangle-exclamation',
        color: 'var(--gold)',
      },
      // Esse status só existe no front
      documentSent: {
        text: t('documentSent.text'),
        tip: t('documentSent.tip'),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--green)',
      },
      analyze: {
        text: t(`analyze.text.${paymentType}`),
        tip: t(`analyze.tip.${paymentType}`),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--blue)',
      },
      review: {
        text: t(`review.text.${paymentType}`),
        tip: t(`review.tip.${paymentType}`),
        icon: 'fa-solid fa-triangle-exclamation',
        color: 'var(--gold)',
      },
      readyToPay: {
        text: t(`readyToPay.text.${paymentType}`),
        tip: t(`readyToPay.tip.${paymentType}`),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--gray)',
      },
      withdrawing: {
        text: t(`withdrawing.text.${paymentType}`),
        tip: t(`withdrawing.tip.${paymentType}`),
        icon: 'fa-solid fa-money-bill-transfer',
        color: 'var(--green)',
      },
      paid: {
        text: t(`paid.text.${paymentType}`),
        tip: t(`paid.tip.${paymentType}`),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--green)',
      },
      paidByFinance: {
        text: t(`paidByFinance.text.${paymentType}`),
        tip: t(`paidByFinance.tip.${paymentType}`),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--green)',
      },
      canceled: {
        text: t(`canceled.text.${paymentType}`),
        tip: t(`canceled.tip.${paymentType}`),
        icon: 'fa=solid fa-clock',
        color: 'var(--gold)',
      },
      retry: {
        text: t(`retry.text.${paymentType}`),
        tip: t(`retry.tip.${paymentType}`),
        icon: 'fa-solid fa-rotate-right',
        color: 'var(--gray)',
      },
      blocked: {
        text: t(`blocked.text.${paymentType}`),
        tip: t(`blocked.tip.${paymentType}`),
        icon: 'fa-solid fa-lock',
        color: 'var(--red)',
      },
      unblocked: {
        text: t(`unblocked.text.${paymentType}`),
        tip: t(`unblocked.tip.${paymentType}`),
        icon: 'fa-solid fa-lock-open',
        color: 'var(--gray)',
      },
      failed: {
        text: t(`failed.text.${paymentType}`),
        tip: t(`failed.tip.${paymentType}`),
        icon: 'fa-solid fa-triangle-exclamation',
        color: 'var(--red)',
      },
      paidByVindi: {
        text: t(`paidByVindi.text.${paymentType}`),
        tip: t(`paidByVindi.tip.${paymentType}`),
        icon: 'fa-solid fa-circle-check',
        color: 'var(--green)',
      },
      analyzeVindi: {
        text: t(`analyzeVindi.text.${paymentType}`),
        tip: t(`analyzeVindi.tip.${paymentType}`),
        icon: 'fa-solid fa-triangle-exclamation',
        color: 'var(--gold)',
      },
    }),
    [t, paymentType]
  )

  if (!paymentStatus?.[state] || !paymentStatus?.[state]?.text) {
    return null
  }

  return (
    <span className="payment-status">
      {paymentStatus?.[state]?.icon ? (
        <i className={`${paymentStatus?.[state]?.icon} mr-2`} style={{ color: paymentStatus?.[state]?.color }}></i>
      ) : null}
      {paymentStatus?.[state]?.text}
      {paymentStatus?.[state]?.tip && !disableTip ? (
        <Tip icon="fa-regular fa-circle-info" color="var(--gray_dark)" fontSize="0.86rem" message={paymentStatus?.[state]?.tip} />
      ) : null}
    </span>
  )
}
